/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { NoisyText } from 'components/noisyText'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import NumberFormat from 'react-number-format'
import { Link } from '@ta-interaktiv/contentbricks'
import Scrollchor from 'react-scrollchor'
import { NoiseScale } from 'components/noiseScale'
import { Testimony } from 'components/testimony'
import { NoiseGraph } from 'components/noiseGraph'
import { testimonies } from './testimonies'

// TODO Set the correct article ID here
const ARTICLE_ID = '232908139260'

export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <div className='top-line'>L'enfer du bruit</div>
            <h1>Le bruit, ennemi public numéro un en ville</h1>
            <h2>
              Une série de mesures nous permet d’analyser plus finement le
              paysage sonore de zones exposées au bruit de la route, et le
              constat est assourdissant.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>Cécile Denayrouse, Duc-Quang Nguyen</div>
            <div className='ta updated'>Mis à jour le 24 mars 2021</div>
          </div>
          <div className='ta text'>
            Bourdonnement incessant des moteurs, démarrages en trombe, coups de
            freins intempestifs, klaxons… Avoir des fenêtres qui donnent sur une
            route fréquentée peut vite devenir un enfer. Cet enfer, c’est
            pourtant le quotidien d’un million de personnes en Suisse, selon
            l’Office fédéral de l’environnement (OFEV). La nuit, une personne
            sur six voit son sommeil perturbé par un bruit routier nuisible ou
            incommodant. Les grandes villes feraient même office de caisses de
            résonance.
          </div>
          <div className='ui icon message folder'>
            <i className='icon'>
              <img src={require('../../images/logo.svg')} />
            </i>
            <div className='content'>
              {/* <div className='header'>Tous nos articles sur le sujet</div> */}
              <ul>
                <li>
                  <div>
                    <b>
                      La sensibilité au bruit est l’effet inattendu du
                      coronavirus
                    </b>
                  </div>
                  <div>
                    <Link href='216768919871'>Lire l'article</Link>
                  </div>
                </li>
                <li>
                  <div>
                    <b>Le bruit des villes perturbe gravement notre sommeil</b>
                  </div>
                  <Link href='415484543720'>Lire l'article</Link>
                </li>
                <li>
                  <div>
                    <b>Trois conseils pour survivre au bruit en appartement</b>
                  </div>
                  <Link href='950033061784'>Lire l'article</Link>
                </li>
              </ul>
            </div>
          </div>
          <div className='ta text'>
            <p>
              Afin de mieux comprendre à quoi sont confrontées les victimes de
              pollution sonore, l’équipe data de la «Tribune de Genève» et de
              «24 heures» a effectué une série de mesures du bruit routier en
              plusieurs points de Genève et Lausanne.
            </p>
            <p>
              Pour y parvenir, des dispositifs développés par une société
              d’ingénierie alémanique ont été installés directement sur les
              rebords de fenêtres de volontaires, le long d’artères fréquentées.
              Durant près de deux mois, microphone face à la rue, ces appareils
              ont enregistré le bruit venant de la route, captant chaque
              seconde, de jour comme de nuit, qu’il pleuve ou qu’il vente. Pas
              moins de 15 millions de turbulentes données ont été ainsi
              collectées, permettant une analyse plus fine du paysage sonore
              citadin.
            </p>
          </div>
        </div>
      </div>

      <NoiseScale />

      <Testimony {...testimonies[0]} />

      <div className='ui vertical fitted segment noise-graph'>
        <div className='ui ta text container'>
          <h4 className='ui text header'>
            Des vrombissements fréquemment supérieurs aux normes légales
          </h4>
          <p>
            Le bruit provoqué par les véhicules à moteur est-il la principale
            cause de pollution sonore en ville? Pour en avoir le cœur net, la
            Tribune de Genève et 24 Heures ont installé des capteurs sur les
            bords de fenêtre de volontaires à Genève et Lausanne. Objectif:
            recueillir durant plusieurs semaines d'affilée, 24h/24, pendant et
            hors confinement, les émissions de décibels provoqués par la
            circulation routière.
          </p>
          <p>
            Le résultat? Une photographie à la fois unique et éclairante du
            bruit des voitures en ville, dont nous vous restituons ici la
            substantifique moelle.
          </p>
        </div>
        <div className='ui vertical segment'>
          <div className='ui container'>
            <div className='ui two column grid doubling stackable'>
              <div className='row'>
                <div className='column special'>
                  <div>
                    <div className='titleBox'>
                      <h4 className='graphicTitle'>
                        Moyenne horaire de volume sonore à Lausanne
                      </h4>
                      <div className='graphicSub'>
                        <div>
                          Chaque{' '}
                          <span className='legend dot'>
                            <img src={require('../../images/legend-dot.svg')} />
                          </span>{' '}
                          point montre la moyenne d'une heure en dB(A). La{' '}
                          <span className='legend line'>
                            <img
                              src={require('../../images/legend-line.svg')}
                            />
                          </span>{' '}
                          courbe montre la moyenne horaire durant deux mois. La{' '}
                          <span className='legend area'>
                            <img
                              src={require('../../images/legend-area.svg')}
                            />
                          </span>{' '}
                          zone orangée montre le seuil légal d’exposition au
                          bruit (limites d'immission).
                        </div>
                        <div style={{ marginTop: '0.5rem' }}>
                          <span className='bold'>
                            Période d'enregistrement:
                          </span>{' '}
                          du 8 décembre 2020 au 11 février 2021
                        </div>
                        <div style={{ marginTop: '0.5rem' }}>
                          <span className='bold'>Emplacement:</span> La rue du
                          Valentin permet de rejoindre l’autoroute. Nos mesures
                          proviennent du balcon au troisième étage au-dessus du
                          giratoire avec l’avenue Alexandre-Vinet. La chaussée
                          est particulièrement pentue en aval et en amont du
                          rond-point.
                        </div>
                      </div>
                    </div>
                    <div className='graphicBox'>
                      <div className='boxWrapper'>
                        <img src={require('../../images/lausanne.svg')} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className='column special'>
                  <div className='titleBox'>
                    <h4 className='graphicTitle'>
                      Moyenne horaire de volume sonore à Genève
                    </h4>
                    <div className='graphicSub'>
                      <div>
                        Chaque{' '}
                        <span className='legend dot'>
                          <img src={require('../../images/legend-dot.svg')} />
                        </span>{' '}
                        point montre la moyenne d'une heure en dB(A). La{' '}
                        <span className='legend line'>
                          <img src={require('../../images/legend-line.svg')} />
                        </span>{' '}
                        courbe montre la moyenne horaire durant deux mois. La{' '}
                        <span className='legend area'>
                          <img src={require('../../images/legend-area.svg')} />
                        </span>{' '}
                        zone orangée montre le seuil légal d’exposition au bruit
                        (limites d'immission).
                      </div>
                      <div style={{ marginTop: '0.5rem' }}>
                        <span className='bold'>Période d'enregistrement:</span>{' '}
                        du 8 décembre 2020 au 11 février 2021
                      </div>
                      <div style={{ marginTop: '0.5rem' }}>
                        <span className='bold'>Emplacement:</span> La rue de
                        Lausanne est connue pour être particulièrement bruyante.
                        Le point d'enregistrement de nos mesures se trouve au
                        bord du trottoir, à mi-chemin entre un arrêt TPG et un
                        feu rouge. Selon le cadastre du bruit routier, il s'agit
                        de l'une des pires portions de route de Genève.
                      </div>
                    </div>
                  </div>
                  <div className='graphicBox'>
                    <div className='boxWrapper'>
                      <img src={require('../../images/geneve-2.svg')} />
                    </div>
                  </div>
                </div>
                <div className='column special'>
                  <div className='titleBox'>
                    <h4 className='graphicTitle'>
                      Moyenne horaire de volume sonore à Genève
                    </h4>
                    <div className='graphicSub'>
                      <div>
                        Chaque{' '}
                        <span className='legend dot'>
                          <img src={require('../../images/legend-dot.svg')} />
                        </span>{' '}
                        point montre la moyenne d'une heure en dB(A). La{' '}
                        <span className='legend line'>
                          <img src={require('../../images/legend-line.svg')} />
                        </span>{' '}
                        courbe montre la moyenne horaire durant deux mois. La{' '}
                        <span className='legend area'>
                          <img src={require('../../images/legend-area.svg')} />
                        </span>{' '}
                        zone orangée montre le seuil légal d’exposition au bruit
                        (limites d'immission).
                      </div>
                      <div style={{ marginTop: '0.5rem' }}>
                        <span className='bold'>Période d'enregistrement:</span>{' '}
                        du 4 août au 2 octobre 2020
                      </div>
                      <div style={{ marginTop: '0.5rem' }}>
                        <span className='bold'>Emplacement:</span> La route de
                        Florissant est un passage quasi obligé pour les
                        pendulaires et les frontaliers. La circulation y est
                        constante. Le rebord de fenêtre d'où proviennent nos
                        mesures se trouve au rez-de-chaussée, à environ 5 mètres
                        du bord de la route.
                      </div>
                    </div>
                  </div>
                  <div className='graphicBox'>
                    <div className='boxWrapper'>
                      <img src={require('../../images/geneve-1.svg')} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4 className='ui text header'>
              Une ordonnance contre le bruit obsolète
            </h4>
            <p>
              Premier constat: dans deux lieux sur trois, le niveau sonore moyen
              dépasse les valeurs limites d’exposition recommandées par
              l'Ordonnance fédérale de protection contre le bruit (OPB), fixées
              à 65 dB(A) le jour et 55 dB(A) la nuit. À Genève I, rue de
              Lausanne, les données récoltées situent le niveau sonore moyen à
              68,62 dB(A) en journée. Lausanne et sa rue du Valentin arrivent en
              deuxième position avec 66,35 dB(A) en moyenne par jour. Seule
              Genève II, route de Florissant, affiche un très raisonnable 64,08
              db(A) de moyenne entre 6h et 22h. «Les mesures récoltées sont
              passionnantes et très instructives, s’enthousiasme Peter Ettler,
              président de la Ligue suisse contre le bruit. Elles démontrent
              bien les limites de l’OPB. Cette ordonnance date des années 80;
              les mesures d’alors avaient été effectuées avec des outils
              préhistoriques en regard de ceux dont nous disposons aujourd’hui.
              L’OPB est obsolète, elle n’est plus du tout adaptée à la vie
              moderne.»
            </p>
            <p>
              La nuit, le constat est encore plus criant puisque passé 22h, tous
              les points de mesure chahutent allègrement la limite de 55 dB(A)
              de l’OPB, avec 64,43 dB(A) à Genève I, 56,37 dB(A) à Genève II et
              60,65 db(A) à Lausanne. Des chiffres déjà élevés alors que les
              lieux culturels et les établissements festifs sont fermés,
              réduisant ainsi la présence de noctambules sur la route. Plus
              inquiétant encore, rue de Lausanne, on s’approche dangereusement
              de la valeur d’alarme de 65 dB(A) la nuit, elle aussi fixée par
              l’OPB.
            </p>
          </div>
        </div>
      </div>

      <Testimony {...testimonies[1]} />

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4 className='ui text header'>
              Les pics de bruit, principale cause de souffrance sonore
            </h4>
            <p>
              La messe est dite? Pas encore. En regardant de plus près les
              moyennes sonores heure par heure, on s’aperçoit que les courbes
              sont composées de multitudes de pics de son. Une analyse fine
              prouve que nos volontaires ont largement de quoi être irrités: la
              valeur d’alarme de l’OPB, fixée à 70 db(A) en journée – soit
              l’équivalent du bruit d’un aspirateur – est en moyenne dépassée
              13’268 fois par jour à Genève I, 4143 fois à Lausanne et 2140 fois
              à Genève II. Selon les spécialistes, ce sont justement ces pics
              sonores qui sont les plus délétères. «Le cerveau humain est
              extraordinaire, il s’habitue au brouhaha. C’est la répétition d’un
              bruit isolé, et qui est perçu par certains comme dérangeant, qui
              crée la pénibilité et la fatigue», explique le Dr Othman Sentissi,
              psychiatre aux Hôpitaux universitaires de Genève.
            </p>
            <p>
              Sous les fenêtres de nos volontaires, les pics s’avèrent si
              fréquents qu’ils peuvent être traduits en «temps de souffrance
              cumulée». Ainsi les riverains de la rue de Lausanne affrontent
              quotidiennement au minimum deux heures de souffrance auditive.
              Certains mauvais jours, cette exposition douloureuse supérieure à
              70 db(A) peut aller jusqu’à huit heures cumulées. Rue du Valentin,
              les perturbations intenses s’étalent de trente minutes à plus de
              cinq heures selon les jours. «Les autorités ne considèrent que les
              moyennes et ne tiennent absolument pas compte des pics de bruit,
              déplore Peter Ettler. Il est temps d’ajouter des mesures
              supplémentaires pour mieux décrire la situation et officialiser
              l’existence de ces pics.»
            </p>
          </div>
        </div>
      </div>

      <Testimony {...testimonies[2]} />

      {/* <NoiseGraph /> */}

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4 className='ui text header'>
              Des gadgets aussi bruyants qu’un avion
            </h4>
            <p>
              En attendant, les raisons de se sentir exténués par des bruits
              soudains se retrouvent partout. Route de Florissant à Genève, le
              lieu le moins bruyant en moyenne, nos capteurs ont constaté un
              tonitruant record de près de 108 db(A) en pleine journée (lire
              témoignage ci-contre). Ailleurs également, des pics à plus de 100
              dB(A) sont très régulièrement enregistrés. «Ces données ne
              m’étonnent malheureusement pas, poursuit Peter Ettler. Très
              souvent, les pics de bruit sont dus au style de conduite et il
              peut aussi s’agir d’incivilités manifestes. Les pots d’échappement
              modifiés par des chauffards frimeurs, par exemple, sont des abus
              qu’il faut punir. Certains de ces gadgets provoquent le même
              niveau de bruit qu’un avion qui se trouverait à 200 mètres
              d’altitude.»
            </p>
            <p>
              Mais ces incivilités pourraient bien disparaître. La semaine
              dernière, le Conseil national a accepté une motion visant à
              réduire drastiquement les nuisances sonores des véhicules. Au
              menu, un catalogue de mesures visant à sanctionner plus
              efficacement les émissions excessives de bruit liées à la
              circulation routière.
            </p>
          </div>
        </div>
      </div>

      <Footer
        hidePortfolioTeaser
        mailTo='cecile.denayrouse@tamedia.ch'
        locale='fr'
        articleId={ARTICLE_ID}
        credits={[
          ['Textes', 'Cécile Denayrouse'],
          ['Analyse des données et graphiques', 'Duc-Quang Nguyen'],
          ['Conception et réalisation', 'Mathieu Rudaz et Océane Haenni']
        ]}
      />
    </div>
  )
}
